import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Upload, message, Image } from 'antd';
import { PlusOutlined, LoadingOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';

import {fileUpload, publicFileUpload} from "../../../../utils/storageUtils";
import {t} from "../../../../i18n";

type FileType = File;

/**
 * 将文件转换为Base64格式
 * @param file 要转换的文件
 * @returns Promise<string> Base64字符串
 */
const getBase64 = (file: FileType): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });

/**
 * ImageUploader组件的属性接口
 */
interface ImageUploaderProps {
    value?: string[];                    // 存储的URL或objectKey数组
    displayUrls?: string[];              // 展示URL数组（可选）
    onChange?: (urls: string[], previewUrls?: string[]) => void; // 值变化回调，第二个参数为预览URL数组
    onError?: (error: Error, file: File) => void; // 错误回调
    /** 上传中状态变化回调，父组件可据此禁用保存等操作 */
    onUploadingChange?: (uploading: boolean) => void;
    maxCount?: number;                   // 最大文件数量
    accept?: string;                     // 接受的文件类型
    maxSizeMB?: number;                  // 最大文件大小
    disabled?: boolean;                  // 是否禁用
    tag?: string;                         // 标签
    usePublicBucket?: boolean;            // 是否上传到公开桶
}

// 默认接受的文件类型
const defaultAccept = '.jpg,.jpeg,.png,.gif';

/**
 * 创建UploadFile对象
 * @param url 文件URL
 * @param index 文件索引
 * @param objectKey 对象键值
 * @returns UploadFile对象
 */
const createFileObject = (url: string, index: number, objectKey: string): UploadFile => {
    // 从 objectKey 中提取文件扩展名，因为 Blob URL 没有扩展名
    const extFromKey = objectKey.includes('.') ? objectKey.substring(objectKey.lastIndexOf('.')) : '.jpg';
    return {
        uid: `-${index}-${objectKey}`, // 使用 objectKey 确保唯一性
        name: `image-${index}${extFromKey}`,
        status: 'done' as const,
        url,
        thumbUrl: url,
        response: { objectKey }
    };
};

/**
 * 从文件列表中提取URL数组
 * @param files UploadFile数组
 * @returns [objectKey数组, 预览URL数组]
 */
const extractFileUrls = (files: UploadFile[]): [string[], string[]] => {
    const doneFiles = files.filter(file => file.status === 'done');
    const objectKeys = doneFiles.map(file => file.response?.objectKey || file.url || file.thumbUrl);
    const previewUrls = doneFiles.map(file => file.url || file.thumbUrl || '');
    return [objectKeys, previewUrls];
};

/**
 * 清理Blob URL，防止内存泄漏
 * @param file UploadFile对象
 */
const cleanupBlobUrl = (file: UploadFile) => {
    if (file.url && file.url.startsWith('blob:')) {
        URL.revokeObjectURL(file.url);
    }
};

/**
 * 上传单个文件
 * @param file 要上传的文件
 * @param onError 错误回调函数
 * @returns Promise<UploadFile> 上传后的文件对象
 */
const uploadSingleFile = async (file: UploadFile, onError?: (error: Error, file: File) => void, tag?: string, usePublicBucket?: boolean): Promise<UploadFile> => {
    if (!file.originFileObj) return file;

    try {
        // 调用文件上传服务（公开桶或私有桶）
        const uploadFn = usePublicBucket ? publicFileUpload : fileUpload;
        const { objectKey } = await uploadFn(file.originFileObj, tag);
        // 创建Blob URL用于预览
        const blobUrl = URL.createObjectURL(file.originFileObj);
        return {
            ...file,
            status: 'done' as const,
            url: blobUrl,        // 使用Blob URL用于预览
            thumbUrl: blobUrl,   // 使用Blob URL用于缩略图
            response: { objectKey }
        };
    } catch (error) {
        // 处理上传错误
        console.error('Upload failed for file:', file.name, error);
        const errorMessage = error instanceof Error ? error.message : t('components.upload.uploadFailed');
        message.error(errorMessage);
        onError?.(error instanceof Error ? error : new Error(t('components.upload.uploadFailed')), file.originFileObj);
        
        return {
            ...file,
            status: 'error' as const,
            url: '',
            thumbUrl: ''
        };
    }
};

/**
 * 图片上传组件
 * 支持多文件上传、预览、删除等功能
 */
const ImageUploader: React.FC<ImageUploaderProps> = ({
    value = [],
    displayUrls = [],
    onChange,
    onError,
    onUploadingChange,
    maxCount = 1,
    accept = defaultAccept,
    maxSizeMB = undefined,
    disabled = false,
    tag = undefined,
    usePublicBucket = false,
}) => {
    // 预览相关状态
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');

    // 文件列表和上传状态
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    // 用 ref 存最新回调，仅当 isUploading 变化时通知父组件，避免父组件每次渲染传入新函数导致重复 setState 甚至循环
    const onUploadingChangeRef = React.useRef(onUploadingChange);
    onUploadingChangeRef.current = onUploadingChange;
    useEffect(() => {
        onUploadingChangeRef.current?.(isUploading);
    }, [isUploading]);

    // 追踪当前组件创建的 Blob URL，用于组件卸载时清理
    const ownedBlobUrlsRef = React.useRef<Set<string>>(new Set());

    /**
     * 从props构建文件列表
     * 处理displayUrls和value的映射关系
     */
    const fileListFromProps = useMemo(() => {
        const hasDisplayUrls = (displayUrls?.length || 0) > 0;
        const hasValue = (value?.length || 0) > 0;
        
        // 如果有displayUrls且与value长度一致，使用displayUrls作为展示URL
        if (hasDisplayUrls && (value?.length || 0) === (displayUrls?.length || 0)) {
            return displayUrls.map((url, index) => createFileObject(url, index, value[index]));
        } 
        // 单文件模式且有value时，优先使用displayUrls，否则使用value
        else if (maxCount === 1 && hasValue) {
            const urls = hasDisplayUrls ? displayUrls : value;
            return urls.map((url, index) => createFileObject(url, index, value[index]));
        }
        return [];
    }, [value, displayUrls, maxCount]);

    /**
     * 当前文件列表
     * 优先使用内部状态，否则使用props构建的列表
     */
    const currentFileList = useMemo(() => {
        const hasInternalState = fileList.length > 0 || 
            (fileList.length === 0 && (value?.length || 0) === 0 && (displayUrls?.length || 0) === 0);
        
        return hasInternalState ? fileList : fileListFromProps;
    }, [fileListFromProps, fileList, value, displayUrls]);

    /**
     * 处理文件预览
     * 支持本地文件和远程文件的预览
     */
    const handlePreview = useCallback(async (file: UploadFile) => {
        // 如果没有URL和预览，且是本地文件，则生成Base64预览
        if (!file.url && !file.preview && file.originFileObj) {
            try {
                file.preview = await getBase64(file.originFileObj as File);
            } catch (error) {
                console.error('Failed to generate preview:', error);
                message.error(t('components.upload.failedToGeneratePreview'));
                return;
            }
        }
        // 设置预览图片并打开预览
        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
    }, []);

    /**
     * 处理文件上传变化
     * 包括文件上传和删除操作
     */
    const handleUploadChange: UploadProps['onChange'] = useCallback(async ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
        // 过滤出需要上传的文件（新选择的文件）
        const filesToUpload = newFileList.filter(file => file.originFileObj && file.status !== 'done' && file.status !== 'error');
        
        if (filesToUpload.length > 0) {
            // 有文件需要上传
            setIsUploading(true);
            try {
                // 只上传需要上传的文件
                const uploadPromises = newFileList.map(file => uploadSingleFile(file, onError, tag, usePublicBucket));
                const updatedFileList = await Promise.all(uploadPromises);

                // 追踪新创建的 Blob URL
                updatedFileList.forEach(file => {
                    if (file.url && file.url.startsWith('blob:')) {
                        ownedBlobUrlsRef.current.add(file.url);
                    }
                });

                setFileList(updatedFileList);
                // 延迟调用onChange，避免在渲染过程中调用
                setTimeout(() => {
                    const [keys, previews] = extractFileUrls(updatedFileList);
                    onChange?.(keys, previews);
                }, 0);
            } finally {
                setIsUploading(false);
            }
        } else {
            // 没有文件需要上传（如删除操作）
            setFileList(newFileList);
            const [keys, previews] = extractFileUrls(newFileList);
            onChange?.(keys, previews);
        }
    }, [onChange, onError, tag, usePublicBucket]);

    /**
     * 文件上传前的验证
     * 检查文件类型和大小
     */
    const beforeUpload = useCallback((file: File) => {
        // 检查文件类型是否为图片
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error(t('components.upload.onlyImageFilesAllowed'));
            return Upload.LIST_IGNORE; // 完全阻止文件被添加
        }

        // 检查文件大小是否超过限制（如果传入了maxSizeMB）
        if (maxSizeMB !== undefined) {
            const isLtMaxSize = file.size / 1024 / 1024 < maxSizeMB;
            if (!isLtMaxSize) {
                message.error(t('components.upload.imageSizeLimit', { maxSize: maxSizeMB }));
                return Upload.LIST_IGNORE; // 完全阻止文件被添加
            }
        }
        return false; // 阻止自动上传，使用自定义上传逻辑
    }, [maxSizeMB]);

    /**
     * 处理文件删除
     * 清理Blob URL并允许删除
     */
    const handleRemove = useCallback((file: UploadFile) => {
        cleanupBlobUrl(file);
        return true;
    }, []);

    /**
     * 当父组件传入的 value/displayUrls 与当前内部 fileList 不一致时，重置内部状态。
     * 这样在外部交换 value 时（如拖拽排序），组件能正确响应。
     */
    useEffect(() => {
        const propKeys = value ?? [];
        const propUrls = displayUrls ?? [];
        const currentKeys = fileList
            .map(f => f.response?.objectKey)
            .filter((k): k is string => Boolean(k));
        const currentUrls = fileList
            .map(f => f.url || f.thumbUrl)
            .filter((u): u is string => Boolean(u));

        // 如果 propKeys 为空，但 fileList 有已上传的内容，说明外部清空了，需要重置
        if (propKeys.length === 0 && currentKeys.length > 0) {
            setFileList([]);
            return;
        }

        // 检查 keys 是否匹配
        const keysMatch =
            propKeys.length === currentKeys.length &&
            propKeys.every((k, i) => k === currentKeys[i]);

        // 检查 displayUrls 是否匹配（用于移动图片场景）
        const urlsMatch =
            propUrls.length === currentUrls.length &&
            propUrls.every((u, i) => u === currentUrls[i]);

        // 如果 keys 或 urls 不匹配，且外部有提供值，则重置让 fileListFromProps 生效
        if (propKeys.length > 0 && (!keysMatch || (propUrls.length > 0 && !urlsMatch))) {
            setFileList([]);
        }
    }, [value, displayUrls]);

    /**
     * 组件卸载时清理当前组件创建的 Blob URL
     * 防止内存泄漏
     * 注意：只清理当前组件创建的 Blob URL，不清理从 displayUrls 传入的，
     * 因为那些 URL 可能被其他组件使用
     */
    useEffect(() => {
        const ownedUrls = ownedBlobUrlsRef.current;
        return () => {
            ownedUrls.forEach(url => {
                URL.revokeObjectURL(url);
            });
            ownedUrls.clear();
        };
    }, []);

    /**
     * 自定义渲染上传项：图片覆盖层中间放预览，右上角放删除
     */
    const renderUploadItem = useCallback<NonNullable<UploadProps['itemRender']>>(
        (_originNode, file, _fileList, actions) => {
            // 上传中的状态直接返回默认节点，保持 loading 样式
            if (file.status && file.status !== 'done' && file.status !== 'error') {
                return _originNode;
            }

            const fileUrl = file.url || file.thumbUrl;

            const handlePreviewClick = (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                handlePreview(file);
            };

            const handleRemoveClick = (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                if (!disabled) {
                    actions?.remove?.();
                }
            };

            return (
                <div className="relative w-full h-full rounded border border-gray-200 overflow-hidden bg-gray-100 group">
                    {fileUrl ? (
                        <img src={fileUrl} alt={file.name} className="w-full h-full object-cover" />
                    ) : (
                        _originNode
                    )}

                    {/* 预览按钮（居中） */}
                    <button
                        type="button"
                        className="absolute inset-0 flex items-center justify-center text-white text-lg opacity-0 group-hover:opacity-100 transition-opacity bg-black/40"
                        onClick={handlePreviewClick}
                        aria-label={t('components.upload.preview') || 'Preview'}
                    >
                        <EyeOutlined />
                    </button>

                    {/* 删除按钮（右上角）- 禁用状态下隐藏 */}
                    {!disabled && (
                        <button
                            type="button"
                            className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={handleRemoveClick}
                            aria-label={t('components.upload.delete') || 'Delete'}
                        >
                            <DeleteOutlined />
                        </button>
                    )}
                </div>
            );
        },
        [handlePreview, disabled]
    );

    /**
     * 上传按钮
     * 根据上传状态显示不同的按钮样式
     */
    const uploadButton = useMemo(() => {
        if (isUploading) {
            // 上传中状态：显示加载图标和文字
            return (
                <button style={{ border: 0, background: 'none' }} type="button">
                    <LoadingOutlined style={{ fontSize: '26px', color: '#1677ff' }} />
                    <div style={{ marginTop: 8, color: '#000000' }}>{t('components.upload.uploadIng')}</div>
                </button>
            );
        }
        // 正常状态：显示加号图标和上传文字
        return (
            <button style={{ border: 0, background: 'none' }} type="button">
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>{t('components.upload.upload')}</div>
            </button>
        );
    }, [isUploading]);

    return (
        <>
            <Upload
                listType="picture-card"
                fileList={currentFileList}
                onPreview={handlePreview}
                onChange={handleUploadChange}
                onRemove={handleRemove}
                showUploadList={{ showPreviewIcon: false, showRemoveIcon: false }}
                itemRender={renderUploadItem}
                maxCount={maxCount}
                accept={accept}
                disabled={disabled || isUploading}
                beforeUpload={beforeUpload}
            >
                {/* 达到最大数量或仅展示模式时不显示上传按钮 */}
                {currentFileList.length >= maxCount || disabled ? null : uploadButton}
            </Upload>
            
            {/* 图片预览组件 */}
            {previewImage && (
                <Image
                    wrapperStyle={{ display: 'none' }}
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: (visible) => setPreviewOpen(visible),
                        afterOpenChange: (visible) => !visible && setPreviewImage(''),
                    }}
                    src={previewImage}
                />
            )}
        </>
    );
};

export default ImageUploader;
